/* wbs-manifesto.js
   Word-by-word scroll reveal, matching Framer "Text Reveal Scroll":
     - dim opacity 0.2
     - start when the text top hits 90% of the viewport
     - finish when the text bottom hits 30% of the viewport
     - range = text height + 60% of the viewport
*/
(function () {
  'use strict';

  function initDialog(section) {
    var dialog = section.querySelector('.wbs-manifesto__dialog');
    var openBtn = section.querySelector('[data-wbs-manifesto-open]');
    if (!dialog) return;

    var viewport = window.visualViewport;

    /* Move the dialog to <body> before opening. On some phones a transformed
       or overflow-hidden ancestor becomes the dialog's containing block, so
       height:fit-content + max-height:100% collapses the form. */
    function mountDialog() {
      if (dialog.parentNode !== document.body) {
        document.body.appendChild(dialog);
      }
    }

    function fitDialog() {
      if (!dialog.open) return;
      var viewHeight = viewport ? viewport.height : window.innerHeight;
      var limit = Math.max(160, Math.round(viewHeight - 32));
      var panel = dialog.querySelector('.wbs-manifesto__dialog-panel');

      if (panel) {
        panel.style.maxHeight = 'none';
        panel.style.overflow = 'visible';
        panel.style.height = 'auto';
      }

      dialog.style.position = 'fixed';
      dialog.style.inset = '0';
      dialog.style.transform = 'none';
      dialog.style.margin = 'auto';
      dialog.style.width = Math.min(560, Math.max(0, window.innerWidth - 32)) + 'px';
      dialog.style.maxHeight = limit + 'px';
      dialog.style.overflow = 'auto';
      /* height:auto with inset:0 stretches to the viewport. Pin the content
         height so a collapsed fit-content box cannot clip the fields. */
      dialog.style.height = 'max-content';

      var contentHeight = dialog.scrollHeight;
      dialog.style.height = Math.min(Math.max(contentHeight, 1), limit) + 'px';
    }

    function openDialog() {
      mountDialog();
      if (typeof dialog.showModal === 'function' && !dialog.open) {
        dialog.showModal();
      }
      fitDialog();
      requestAnimationFrame(fitDialog);
    }

    function closeDialog() {
      if (dialog.open) dialog.close();
    }

    if (openBtn) {
      openBtn.addEventListener('click', openDialog);
    }

    section.querySelectorAll('[data-wbs-manifesto-close]').forEach(function (btn) {
      btn.addEventListener('click', closeDialog);
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeDialog();
    });

    function onViewportChange() {
      fitDialog();
    }

    window.addEventListener('resize', onViewportChange);
    if (viewport) viewport.addEventListener('resize', onViewportChange);

    document.addEventListener('shopify:section:unload', function (event) {
      if (!event.target || !event.target.contains(section)) return;
      window.removeEventListener('resize', onViewportChange);
      if (viewport) viewport.removeEventListener('resize', onViewportChange);
      dialog.remove();
    });

    if (section.getAttribute('data-wbs-manifesto-reopen-dialog') === 'true') {
      openDialog();
    }
  }

  function init(section) {
    initDialog(section);

    var textEl = section.querySelector('.wbs-manifesto__text');
    if (!textEl) return;

    /* ── 1. Split every text node into per-word spans ─────────────── */
    splitWords(textEl);

    var words = Array.prototype.slice.call(
      section.querySelectorAll('.wbs-manifesto__word')
    );
    if (!words.length) return;

    var total = words.length;

    /* ── 2. Scroll handler ────────────────────────────────────────── */
    var ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    function update() {
      ticking = false;
      var rect = textEl.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var start = vh * 0.9;
      var end = vh * 0.3;
      var range = rect.height + (start - end);
      var progress = range > 0 ? (start - rect.top) / range : 0;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      var scaled = progress * total;
      var lit = Math.floor(scaled);
      var fraction = scaled - lit;

      for (var i = 0; i < total; i++) {
        if (i < lit) words[i].style.opacity = '1';
        else if (i === lit) words[i].style.opacity = String(0.2 + fraction * 0.8);
        else words[i].style.opacity = '0.2';
      }
    }

    var desktopScroll = window.matchMedia('(min-width: 990px)');
    var scrollTarget = null;
    var boundLenis = null;

    function bindScrollTarget() {
      var next = desktopScroll.matches
        ? document.querySelector('.page-wrapper') || window
        : window;
      if (scrollTarget === next) return;
      if (scrollTarget) scrollTarget.removeEventListener('scroll', onScroll);
      scrollTarget = next;
      scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    }

    function bindLenis() {
      if (!window.lenis || boundLenis === window.lenis) return;
      if (boundLenis) boundLenis.off('scroll', onScroll);
      boundLenis = window.lenis;
      window.lenis.on('scroll', onScroll);
    }

    bindScrollTarget();
    bindLenis();
    document.addEventListener('wbs:lenis', bindLenis);
    desktopScroll.addEventListener('change', function () {
      bindScrollTarget();
      onScroll();
    });
    update();
  }

  /* ── Word splitter ──────────────────────────────────────────────── */
  function splitWords(root) {
    /* Collect all leaf text nodes that aren't pure whitespace */
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) textNodes.push(node);
    }

    textNodes.forEach(function (tn) {
      var parent = tn.parentNode;
      var parts = tn.nodeValue.split(/(\s+)/);
      var frag = document.createDocumentFragment();
      parts.forEach(function (part) {
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
        } else if (part) {
          var span = document.createElement('span');
          span.className = 'wbs-manifesto__word';
          span.textContent = part;
          span.style.opacity = '0.2';
          span.style.willChange = 'opacity';
          frag.appendChild(span);
        }
      });
      parent.replaceChild(frag, tn);
    });
  }

  /* ── Boot ───────────────────────────────────────────────────────── */
  function boot() {
    var sections = document.querySelectorAll('[data-wbs-manifesto]');
    Array.prototype.forEach.call(sections, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
